import type { Table } from '@tanstack/react-table'
import pluralize from 'pluralize'

import {
  ProcessButton,
  ProjectStateBar,
  useProject,
  useStatementTally,
} from '@/modules/projects'
import {
  hasProjectFailed,
  isProjectProcessing,
} from '@/modules/projects/status'

interface ProcessPanelProps {
  table: Table<any> | null
}

export const ProcessPanel = ({ table }: ProcessPanelProps) => {
  const project = useProject()
  const { statementCount, totalPages } = useStatementTally()

  const isEmpty = statementCount === 0
  const isProcessing = isProjectProcessing(project)

  const label = hasProjectFailed(project)
    ? 'Retry all failed statements'
    : isEmpty
      ? 'Process statements'
      : `Process ${statementCount} ${pluralize('statement', statementCount)}`

  return (
    <div className="@4xl:sticky @4xl:top-6 @4xl:max-h-[calc(100vh-3rem)] @4xl:overflow-y-auto">
      <div className="flex items-center justify-between gap-4 border bg-white px-5 py-3 shadow-lg @4xl:flex-col @4xl:items-stretch rounded-lg @4xl:p-5 @4xl:shadow-none">
        <div className="min-w-0">
          <h3 className="hidden font-semibold leading-6 tracking-tight @4xl:block">
            Ready to process
          </h3>
          <p className="hidden text-sm text-muted-foreground @4xl:mt-1 @4xl:block">
            {isEmpty
              ? 'Upload PDFs to enable processing.'
              : 'Extraction starts on every uploaded statement.'}
          </p>

          {!isEmpty && (
            <output className="block truncate text-sm tabular-nums text-muted-foreground @4xl:mt-4">
              <span className="font-semibold text-foreground">
                {statementCount} {pluralize('statement', statementCount)}
              </span>{' '}
              · {totalPages} {pluralize('page', totalPages)}
            </output>
          )}
        </div>

        {isProcessing && (
          <ProjectStateBar
            compact
            alwaysVisible
            className="hidden @4xl:block"
          />
        )}

        <ProcessButton
          table={table}
          className="@4xl:w-full"
          messagePlacement="block"
        >
          {label}
        </ProcessButton>
      </div>
    </div>
  )
}
